import { useState } from "react";
import { usePortal, type ClientDetail, type MemberRow, type UpdateMemberBody } from "@app/portal-sdk";
import { Alert, AppBadge, Button, DataTable, EmptyState, IconButton, Menu, MenuItem, Stack, Typography } from "@app/ui-kit";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Snackbar from "@mui/material/Snackbar";
import { LoadingScreen } from "../../components/LoadingScreen";
import { displayName, formatDate } from "../../lib/format";
import { errorMessage, useApiQuery } from "../../lib/useApiQuery";
import { InviteDialog } from "../InviteDialog";

type Props = { client: ClientDetail };

const statusBadge = (status: MemberRow["status"]) => {
  switch (status) {
    case "active":
      return <AppBadge label="Active" color="success" />;
    case "invited":
      return <AppBadge label="Invited" color="info" />;
    default:
      return <AppBadge label="Disabled" />;
  }
};

const roleLabel = (role: MemberRow["role"]) => (role === "client_admin" ? "Client admin" : "Member");

export const MembersTab = ({ client }: Props) => {
  const { api } = usePortal();
  const members = useApiQuery<MemberRow[]>(
    () => api.request<MemberRow[]>(`/admin/clients/${client.id}/members`),
    [api, client.id]
  );
  const [inviteOpen, setInviteOpen] = useState(false);
  const [menu, setMenu] = useState<{ anchor: HTMLElement; member: MemberRow } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const replaceRow = (row: MemberRow) =>
    members.setData((rows) => (rows ?? []).map((r) => (r.membershipId === row.membershipId ? row : r)));

  const act = async (label: string, fn: () => Promise<MemberRow>) => {
    setBusy(true);
    setError(null);
    setMenu(null);
    try {
      replaceRow(await fn());
      setToast(label);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const resend = (m: MemberRow) =>
    act(`Invitation resent to ${m.user.email}`, () =>
      api.request<MemberRow>(`/admin/clients/${client.id}/members/${m.membershipId}/resend`, { method: "POST" })
    );

  const update = (m: MemberRow, body: UpdateMemberBody, label: string) =>
    act(label, () =>
      api.request<MemberRow>(`/admin/clients/${client.id}/members/${m.membershipId}`, { method: "PATCH", json: body })
    );

  const inviteButton = (
    <Button variant="contained" onClick={() => setInviteOpen(true)}>
      Invite
    </Button>
  );

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          People who can open this client's portal. Invitations are sent by email with a temporary password.
        </Typography>
        {inviteButton}
      </Stack>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      {members.error && <Alert severity="error">{members.error}</Alert>}

      {members.loading && !members.data ? (
        <LoadingScreen minHeight={200} />
      ) : (members.data ?? []).length === 0 ? (
        <EmptyState eyebrow="No Members" title="Nobody has been invited yet" action={inviteButton} />
      ) : (
        <DataTable
          columns={[
            { key: "name", label: "Name" },
            { key: "email", label: "Email" },
            { key: "role", label: "Role" },
            { key: "status", label: "Status" },
            { key: "invited", label: "Invited" },
            { key: "lastLogin", label: "Last sign-in" },
            { key: "actions", label: "" }
          ]}
          rows={(members.data ?? []).map((m) => ({
            name: <Typography variant="subtitle2">{displayName(m.user)}</Typography>,
            email: m.user.email,
            role: roleLabel(m.role),
            status: statusBadge(m.status),
            invited: formatDate(m.invitedAt),
            lastLogin: formatDate(m.lastLoginAt, "never"),
            actions: (
              <IconButton size="small" onClick={(event) => setMenu({ anchor: event.currentTarget, member: m })} disabled={busy}>
                <MoreVertIcon fontSize="small" />
              </IconButton>
            )
          }))}
        />
      )}

      <Menu anchorEl={menu?.anchor ?? null} open={Boolean(menu)} onClose={() => setMenu(null)}>
        {menu?.member.status === "invited" && (
          <MenuItem onClick={() => void resend(menu.member)}>Resend invitation</MenuItem>
        )}
        {menu && (
          <MenuItem
            onClick={() =>
              void update(
                menu.member,
                { role: menu.member.role === "client_admin" ? "member" : "client_admin" },
                "Role updated"
              )
            }
          >
            {menu.member.role === "client_admin" ? "Make member" : "Make client admin"}
          </MenuItem>
        )}
        {menu && menu.member.status !== "disabled" && (
          <MenuItem onClick={() => void update(menu.member, { status: "disabled" }, "Access disabled")}>
            Disable access
          </MenuItem>
        )}
        {menu && menu.member.status === "disabled" && (
          <MenuItem onClick={() => void update(menu.member, { status: "active" }, "Access restored")}>
            Re-enable access
          </MenuItem>
        )}
      </Menu>

      <InviteDialog
        open={inviteOpen}
        clientId={client.id}
        clientName={client.name}
        onClose={() => setInviteOpen(false)}
        onInvited={(row) => {
          members.setData((rows) => [...(rows ?? []).filter((r) => r.membershipId !== row.membershipId), row]);
          setToast(`Invitation sent to ${row.user.email}`);
        }}
      />

      <Snackbar open={Boolean(toast)} autoHideDuration={3500} onClose={() => setToast(null)} message={toast} />
    </Stack>
  );
};
